import { useState } from 'react'
import { useRefresh, Record } from 'ra-core'
import {
    Datagrid,
    useTranslate,
    FunctionField,
    TextField,
    ArrayField,
    TopToolbar
} from 'react-admin'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography
} from '@material-ui/core'
import DeleteIcon from '@mui/icons-material/Delete'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@mui/icons-material/Add'
import styled from 'styled-components'
import { ChoiceType } from 'types'
import _ from 'lodash'

import datagridStyles from '../../layout/styles/datagridStyles'
import DatagridPublishedColumnContent from '../../components/common/DatagridPublishedColumnContent'
import AutoCompleteSelectWithData from 'components/formfields/AutoCompleteSelectWithData'
import WarningDialog from 'components/modelManager/molecules/popups/WarningDialog'

import { useGetAllPackagesQuery } from 'apollo/package_manager/queries/GetAllPackages.generated'
import { usePackagesCreatePackageGroupPackageMutation } from 'apollo/package_manager/mutations/PackagesCreatePackageGroupPackage.generated'
import { usePackagesDeletePackageGroupPackageMutation } from 'apollo/package_manager/mutations/PackagesDeletePackageGroupPackage.generated'

const useStyles = makeStyles({
    dialogActions: {
        padding: 0,
        paddingBottom: '1rem'
    },
    deleteIcon: {
        '&:hover': {
            transform: `scale(1.1)`
        }
    }
})

const PackageGroupPackageList = (props) => {
    const [isOpenedPopup, setIsOpenedPopup] = useState<boolean>(false)
    const [isOpenedConfirmPopup, setIsOpenedConfirmPopup] = useState<boolean>(false)
    const [deletePackageID, setDeletePackageID] = useState<number>(0)
    const [selectedPackageId, setSelectedPackageId] = useState(0)

    const refresh = useRefresh()
    const translate = useTranslate()
    const datagridClasses = datagridStyles()
    const classes = useStyles()

    const { data: packages } = useGetAllPackagesQuery({
        variables: { first: 10000 }
    })

    // Filter all unique packages (packages that are not already added)
    const filteredPackages = _.differenceBy(
        packages?.packagesPackages?.data,
        props?.record?.packages,
        'id'
    )

    const [packagesCreatePackageGroupPackageMutation] =
        usePackagesCreatePackageGroupPackageMutation()

    const [packagesDeletePackageGroupPackageMutation] =
        usePackagesDeletePackageGroupPackageMutation()

    const toggleModal = () => {
        setIsOpenedPopup(!isOpenedPopup)
    }

    const handleAutocompleteChange = (selectedItem) => {
        setSelectedPackageId(selectedItem?.id)
    }

    const handleCloseConfirmPopup = () => {
        setIsOpenedConfirmPopup(false)
    }

    const handleContinueConfirmPopup = () => {
        packagesDeletePackageGroupPackageMutation({
            variables: { groupId: +props?.record?.id, packageId: deletePackageID }
        })
        refresh()
    }

    const handleAddRelationFormSubmit = () => {
        packagesCreatePackageGroupPackageMutation({
            variables: {
                groupId: +props?.record?.id,
                packageId: +selectedPackageId
            }
        })
        setIsOpenedPopup(false)
        refresh()
    }

    const handleDeleteClick = (event, id: number) => {
        event?.stopPropagation() // Stops rowClick from firing
        setDeletePackageID(id)
        setIsOpenedConfirmPopup(true)
    }

    return (
        <>
            <ListWrapper>
                <TopToolbar>
                    <Button color="primary" variant={'outlined'} onClick={toggleModal}>
                        <AddIcon />
                        {translate('manager.resources.packageGroup.package.add')}
                    </Button>
                </TopToolbar>
                <ArrayField source="packages">
                    <Datagrid
                        classes={datagridClasses}
                        rowClick={(_, __, record) => `/Package/${record.id}`}
                    >
                        <TextField
                            source="public_name"
                            label={translate('manager.resources.packageGroup.package.name')}
                        />
                        <TextField
                            source="type"
                            label={translate('manager.resources.packageGroup.package.type')}
                        />
                        <FunctionField
                            source="active"
                            label={translate('manager.resources.general.status')}
                            render={(record?: Record | undefined) => (
                                <DatagridPublishedColumnContent record={record} />
                            )}
                        />
                        <FunctionField
                            label={translate('manager.resources.general.actions')}
                            render={(record?: Record | undefined) =>
                                record?.id && (
                                    <Tooltip title={translate('manager.resources.general.delete')}>
                                        <DeleteIcon
                                            className={classes.deleteIcon}
                                            onClick={(event) =>
                                                handleDeleteClick(event, +record?.id)
                                            }
                                        />
                                    </Tooltip>
                                )
                            }
                        />
                    </Datagrid>
                </ArrayField>
            </ListWrapper>

            <Dialog fullWidth open={isOpenedPopup} onClose={() => toggleModal()}>
                <DialogTitle>
                    <Typography variant="h6" gutterBottom>
                        {translate('manager.resources.packageGroup.package.add')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {filteredPackages.length === 0 && (
                        <Typography
                            variant="caption"
                            display="block"
                            gutterBottom
                            color="textSecondary"
                        >
                            {translate('manager.resources.packageGroup.package.hint')}
                        </Typography>
                    )}
                    <AutoCompleteSelectWithData
                        required
                        format
                        source="package_id"
                        data={filteredPackages ?? []}
                        customChoices={
                            (filteredPackages &&
                                filteredPackages.map(
                                    (item): ChoiceType => ({
                                        id: item?.id,
                                        name: `${item?.public_name} | ${item?.type}` ?? ''
                                    })
                                )) ??
                            []
                        }
                        onSelect={handleAutocompleteChange}
                        disabled={filteredPackages.length === 0}
                    />
                    <DialogActions className={classes.dialogActions}>
                        <Button onClick={toggleModal} variant="contained" color="default">
                            {translate('manager.resources.packageGroup.package.close')}
                        </Button>
                        <Button
                            onClick={handleAddRelationFormSubmit}
                            variant="contained"
                            color="primary"
                            disabled={selectedPackageId === 0}
                        >
                            {translate('manager.resources.packageGroup.package.add_button')}
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>

            <WarningDialog
                open={isOpenedConfirmPopup}
                handleClose={handleCloseConfirmPopup}
                handleContinue={handleContinueConfirmPopup}
                maxWidth="md"
                title={translate(
                    'manager.resources.packageGroup.package.delete_confirm_popup.title'
                )}
                content={translate(
                    'manager.resources.packageGroup.package.delete_confirm_popup.content'
                )}
            />
        </>
    )
}

const ListWrapper = styled.section`
    width: 100%;
    padding-bottom: 2rem;

    & .MuiPaper-root {
        border: 0;
    }
`

export default PackageGroupPackageList
